import React, { useMemo, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { useUnaALaVez } from "../../../../utils/unaALaVez";
import horarioBonito from "../../../../utils/horarioBonito";
import { FechaDMY, FechaHoraDMY, HoraAmPm } from "../../../../components/fechas/camposFecha";
import { horarioAmPm } from "../../../../utils/fechaHoraAmPm";
import { serverError } from "../../../../components/modals/payments/register/registerPaymentModal";

/*
	Editor de promociones flexibles (Descuentos › Catálogo; docs/ESPEC_PROMOS_FLEXIBLES.md). Todo configurable sin programador:
	1 A qué cursos (lista REAL del plantel: todos / estos / todos menos estos; horarios, periodos, modalidad, duración) ·
	2 Para quién · 3 Qué da (%, monto, precio fijo, meses regalados «paga N regala M» solo en cursos de N+M meses, en qué
	mensualidades) · 4 Cuándo (inicio y fin con hora local del plantel, días, franja) · 5 Cupos (día/semana/mes/año/total, por plantel
	o compartido, por asesora) · 6 Quién la da y si pide aprobación · 7 No se combina con… · versiones e historial.
	Todo lo valida el servidor; aquí solo se arma. Teléfono: una columna, casillas y botones de 44 px, letra de 16 px.
*/

const http = HttpManager.getInstance();
type Promo = any;

export const GRUPOS: Record<string, string> = { mensualidad: "Mensualidades", inscripcion: "Inscripción / Matrícula", referido: "Para Quien Refiere", producto: "Kits y Productos" };
export const VINCULOS: Record<string, string> = { segundo: "2da Persona / 2do Curso", grupo: "Grupo", dos_x_uno: "2x1 (curso base)", referido_por: "Referido por", refiere: "Refiere a Otro" };
export const AUDIENCIAS: Record<string, string> = { nuevo: "Solo Nuevos", reingreso: "Solo Reingresos", actual: "Alumnos Actuales (2do curso)" };
const DIAS: [number, string][] = [[1, "Lu"], [2, "Ma"], [3, "Mi"], [4, "Ju"], [5, "Vi"], [6, "Sá"], [7, "Do"]];
/** "2032-09-01" → "01/09/2032". */
const dmy = (d: string | null | undefined) => (d && /^\d{4}-\d{2}-\d{2}/.test(d) ? d.slice(8, 10) + "/" + d.slice(5, 7) + "/" + d.slice(0, 4) : d || "");
const PERIODOS: Record<string, string> = { dia: "Por Día", semana: "Por Semana", mes: "Por Mes", anio: "Por Año", total: "En Total" };

type Draft = {
	id?: number; name: string; group: string; que: string; value: string; monthsPaid: string; monthsFree: string; monthFrom: string; monthTo: string;
	requirePaidMonths: string; allMonthsInPayment: boolean; freeSecond: boolean; minMonthsInPayment: string;
	cursosModo: "todos" | "estos" | "menos"; courseIds: number[]; levelIds: number[]; excludeCourseIds: number[]; excludeLevelIds: number[];
	talleres: boolean; excludeTalleres: boolean;
	scheduleIds: number[]; periodIds: number[]; modality: string; monthsTotal: string; periodFrom: string;
	audience: string; linkType: string; groupSize: string;
	startsAt: string; endsAt: string; weekdays: number[]; timeFrom: string; timeTo: string;
	quotaLimit: string; quotaPeriod: string; quotaScope: string; quotaKey: string; quotaPerAdvisor: string;
	givenBy: string; requiresApproval: boolean; notCombinable: string[]; active: boolean; conditions: string; reason: string;
};

const toDraft = (p: Promo | null): Draft => {
	const r = (p && p.rules) || {};
	const multimes = !!(r.monthsPaid || r.freeLast);
	return {
		id: p?.id, name: p?.name || "", group: p?.group || "mensualidad",
		que: multimes ? "multimes" : (p?.calcType || "percent"), value: p ? String(p.value) : "",
		monthsPaid: r.monthsPaid ? String(r.monthsPaid) : (r.monthsTotal && r.freeLast ? String(r.monthsTotal - r.freeLast) : ""),
		monthsFree: r.monthsFree ? String(r.monthsFree) : (r.freeLast ? String(r.freeLast) : ""),
		monthFrom: r.monthFrom ? String(r.monthFrom) : "", monthTo: r.monthTo ? String(r.monthTo) : "", requirePaidMonths: r.requirePaidMonths ? String(r.requirePaidMonths) : "",
		allMonthsInPayment: !!r.allMonthsInPayment, freeSecond: !!r.freeSecond, minMonthsInPayment: r.minMonthsInPayment ? String(r.minMonthsInPayment) : "",
		cursosModo: (r.courseIds || []).length || (r.levelIds || []).length ? "estos" : ((r.excludeCourseIds || []).length || (r.excludeLevelIds || []).length ? "menos" : "todos"),
		courseIds: r.courseIds || [], levelIds: r.levelIds || [], excludeCourseIds: r.excludeCourseIds || [], excludeLevelIds: r.excludeLevelIds || [],
		talleres: !!r.talleres, excludeTalleres: !!r.excludeTalleres,
		scheduleIds: r.scheduleIds || [], periodIds: r.periodIds || [], modality: r.modality || "", monthsTotal: !multimes && r.monthsTotal ? String(r.monthsTotal) : "",
		periodFrom: r.periodFrom || "", audience: p?.audience || "", linkType: p?.linkType || "", groupSize: p?.groupSize ? String(p.groupSize) : "",
		startsAt: (p?.startsAt || "").replace(" ", "T"), endsAt: (p?.endsAt || "").replace(" ", "T"), weekdays: p?.weekdays || [], timeFrom: p?.timeFrom || "", timeTo: p?.timeTo || "",
		quotaLimit: p?.quotaLimit ? String(p.quotaLimit) : "", quotaPeriod: p?.quotaPeriod || "", quotaScope: p?.quotaScope || "plantel", quotaKey: p?.quotaKey || "",
		quotaPerAdvisor: p?.quotaPerAdvisor ? String(p.quotaPerAdvisor) : "", givenBy: p?.givenBy || (p?.scope === "caja" ? "caja" : "asesora"),
		requiresApproval: !!p?.requiresApproval, notCombinable: p?.notCombinable || [], active: p ? !!p.active : true, conditions: p?.conditions || "", reason: ""
	};
};

const Casillas: React.FC<{ opciones: { id: any; label: string; sub?: string }[]; elegidos: any[]; onChange: (v: any[]) => void; disabled?: boolean; alto?: number }> =
	({ opciones, elegidos, onChange, disabled, alto }) => (
		<div className="wdCasillas" style={{ maxHeight: alto || 220 }}>
			{opciones.length ? opciones.map((o) => (
				<label key={"c" + o.id} className="wdCasilla">
					<input type="checkbox" checked={elegidos.includes(o.id)} disabled={disabled}
						onChange={(e) => onChange(e.target.checked ? [...elegidos, o.id] : elegidos.filter((x) => x !== o.id))} />
					<span>{o.label}{o.sub ? <small> · {o.sub}</small> : null}</span>
				</label>
			)) : <div className="wdTenue" style={{ fontSize: 13, padding: 6 }}>Nada que elegir.</div>}
		</div>
	);

export const EditorPromo: React.FC<{ campusId: number; promo: Promo | null; cursos: any; codigos: Record<string, string>; onSaved: () => void; onCancel: () => void }> =
	({ campusId, promo, cursos, codigos, onSaved, onCancel }) => {
		const [d, setD] = useState<Draft>(toDraft(promo));
		const [error, setError] = useState<string | null>(null);
		const [busy, setBusy] = useState(false);
		const [buscar, setBuscar] = useState("");
		const set = (k: keyof Draft, v: any) => setD((x) => ({ ...x, [k]: v }));
		const cursosLista = (cursos?.courses || []) as any[];
		const nombreModulo = useMemo(() => {
			const m: Record<number, string> = {};
			cursosLista.forEach((c) => (c.levels || []).forEach((l: any) => { m[l.id] = c.name + " · " + l.name; }));
			return m;
		}, [cursosLista]);
		const filtrados = cursosLista.filter((c) => !buscar.trim() || c.name.toLowerCase().includes(buscar.trim().toLowerCase()));
		const elegidosCurso = d.cursosModo === "menos" ? d.excludeCourseIds : d.courseIds;
		const horarios = ((cursos?.schedules || []) as any[]).filter((s) => d.cursosModo !== "estos" || !d.courseIds.length || d.courseIds.includes(s.courseId));

		const unaVez = useUnaALaVez();
		const save = () => unaVez(saveAhora);
		const saveAhora = async () => {
			try {
				setBusy(true); setError(null);
				const multimes = d.que === "multimes";
				const rules: any = {
					monthFrom: d.monthFrom, monthTo: d.monthTo, periodFrom: d.periodFrom, allMonthsInPayment: d.allMonthsInPayment, freeSecond: d.freeSecond,
					requirePaidMonths: d.requirePaidMonths, minMonthsInPayment: d.minMonthsInPayment, modality: d.modality || null,
					monthsTotal: multimes ? "" : d.monthsTotal, monthsPaid: multimes ? d.monthsPaid : "", monthsFree: multimes ? d.monthsFree : "",
					// [0] = «ningún curso» (lo dejó la migración): se manda tal cual mientras no se elija otro curso; nunca se vuelve «todos».
					courseIds: d.cursosModo === "estos" ? (d.courseIds.some((x) => x > 0) ? d.courseIds.filter((x) => x > 0) : d.courseIds) : [],
					levelIds: d.cursosModo === "estos" ? d.levelIds : [],
					excludeCourseIds: d.cursosModo === "menos" ? d.excludeCourseIds : [], excludeLevelIds: d.cursosModo === "menos" ? d.excludeLevelIds : [],
					talleres: d.cursosModo === "estos" && d.talleres, excludeTalleres: d.cursosModo === "menos" && d.excludeTalleres,
					scheduleIds: d.scheduleIds, periodIds: d.periodIds
				};
				await http.paymentService.savePromotion(campusId, {
					id: d.id, name: d.name, group: d.group, scope: d.givenBy === "caja" ? "caja" : "acuerdo", calcType: multimes ? "percent" : d.que,
					value: multimes ? 100 : Number(d.value), audience: d.audience || null, linkType: d.linkType || null, groupSize: d.groupSize ? Number(d.groupSize) : null,
					startsAt: d.startsAt || null, endsAt: d.endsAt || null, weekdays: d.weekdays, timeFrom: d.timeFrom || null, timeTo: d.timeTo || null,
					quotaLimit: d.quotaLimit || null, quotaPeriod: d.quotaPeriod || null, quotaScope: d.quotaScope, quotaKey: d.quotaKey || null, quotaPerAdvisor: d.quotaPerAdvisor || null,
					givenBy: d.givenBy, requiresApproval: d.requiresApproval, notCombinable: d.notCombinable,
					active: d.active, conditions: d.conditions, reason: d.reason, rules
				});
				onSaved();
			} catch (e) {
				setError(serverError(e));
			} finally {
				setBusy(false);
			}
		};
		const campo = (label: string, el: React.ReactNode) => <label className="wdCampo"><span>{label}</span>{el}</label>;
		// Campos de varias piezas (fecha + hora + am/pm): en un <label> el clic se iba al primer campo; van en un <div>.
		const campoVarios = (label: string, el: React.ReactNode) => <div className="wdCampo"><span>{label}</span>{el}</div>;
		// «Solo estos» / «todos menos estos» sin ningún curso elegido NO se guarda (se volvería «todos los cursos» sin querer).
		const cursosOk = d.cursosModo === "todos" || (d.cursosModo === "estos" ? d.courseIds.length + d.levelIds.length : d.excludeCourseIds.length + d.excludeLevelIds.length) > 0;
		const ningunCurso = d.cursosModo === "estos" && d.courseIds.length > 0 && d.courseIds.every((x) => x === 0) && !d.levelIds.length;
		const franjaCruza = !!d.timeFrom && !!d.timeTo && d.timeTo < d.timeFrom;
		const ampm = (hhmm: string) => { const [h, m] = hhmm.split(":").map(Number); return (h % 12 === 0 ? 12 : h % 12) + ":" + String(m).padStart(2, "0") + (h < 12 ? " am" : " pm"); };
		// Lo que falta para guardar, dicho junto al botón: antes Guardar se quedaba apagado sin decir por qué (2026-10-25).
		const faltan: string[] = [];
		if (d.name.trim().length < 3) faltan.push("el Nombre (3 letras o más)");
		if (d.que === "multimes" ? !(Number(d.monthsPaid) > 0 && Number(d.monthsFree) > 0) : !(Number(d.value) > 0)) {
			faltan.push(d.que === "multimes" ? "cuántos meses paga y cuántos regala" : "el valor del descuento (mayor que cero)");
		}
		if (!cursosOk) faltan.push(d.cursosModo === "estos" ? "elegir al menos un curso" : "elegir qué cursos quedan fuera");
		if (d.timeFrom && d.timeTo && d.timeFrom === d.timeTo) faltan.push("que la franja no empiece y termine a la misma hora");
		const motivo = d.reason.trim().length;
		if (promo && motivo < 10) faltan.push("escribir Por Qué Cambia (10 letras o más" + (motivo ? ", llevas " + motivo : "") + ")");
		const valido = faltan.length === 0;

		return (
			<div className="wdEditor wdEditorPromo">
				<b>{promo ? "Cambiar «" + promo.name + "»" + (promo.version ? " · versión " + promo.version : "") : "Promoción Nueva"}</b>
				{promo && promo.liveAgreements > 0 ? <div className="wdNota">{promo.liveAgreements} matrículas la tienen firmada: <b>no cambian</b> (guardan su copia); esto vale para lo que se apruebe de aquí en adelante.</div> : null}

				<h6 className="wdSub">Nombre y Tipo</h6>
				<div className="wdGrid">
					{campo("Nombre", <input className="form-control" value={d.name} onChange={(e) => set("name", e.target.value)} />)}
					{campo("A Qué Se Aplica", <select className="form-select" value={d.group} onChange={(e) => set("group", e.target.value)}>
						{Object.keys(GRUPOS).map((k) => <option key={k} value={k}>{GRUPOS[k]}</option>)}</select>)}
				</div>

				<h6 className="wdSub">1 · A Qué Cursos Aplica</h6>
				<div className="wdOpciones">
					{([["todos", "Todos los Cursos"], ["estos", "Solo Estos Cursos"], ["menos", "Todos Menos Estos"]] as [Draft["cursosModo"], string][]).map(([k, l]) => (
						<button key={k} type="button" className={"wdToggle" + (d.cursosModo === k ? " wdToggleOn" : "")} onClick={() => set("cursosModo", k)}>{l}</button>
					))}
				</div>
				{d.cursosModo !== "todos" ? (
					<>
						<input className="form-control" style={{ marginTop: 6 }} placeholder="Buscar curso" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
						<Casillas opciones={filtrados.map((c) => ({ id: c.id, label: c.name, sub: [c.modality === "online" ? "en línea" : "presencial", c.months?.length ? c.months.join(", ") + (c.months.length === 1 && c.months[0] === 1 ? " mes" : " meses") : ""].filter(Boolean).join(" · ") }))}
							elegidos={elegidosCurso} onChange={(v) => set(d.cursosModo === "menos" ? "excludeCourseIds" : "courseIds", v.filter((x: number) => x !== 0))} />
						{ningunCurso ? <div className="wdAviso" style={{ fontSize: 13 }}>Hoy no aplica a <b>ningún curso</b> (así venía de antes): elige los cursos o cámbiala a «Todos los Cursos».</div> : null}
						{!cursosOk ? <div className="wdAviso" style={{ fontSize: 13 }}>Elige al menos un curso{d.cursosModo === "menos" ? " para quitar" : ""}, o cámbiala a «Todos los Cursos».</div> : null}
						<label className="wdCasilla"><input type="checkbox" checked={d.cursosModo === "menos" ? d.excludeTalleres : d.talleres}
							onChange={(e) => set(d.cursosModo === "menos" ? "excludeTalleres" : "talleres", e.target.checked)} />
							<span>Y sus talleres <small>(también los que se abran después)</small></span></label>
						{(d.cursosModo === "estos" ? d.levelIds : d.excludeLevelIds).length ? (
							<div className="wdChips">
								<span className="wdTenue" style={{ fontSize: 13 }}>Módulos sueltos:</span>
								{(d.cursosModo === "estos" ? d.levelIds : d.excludeLevelIds).map((id) => (
									<button key={"l" + id} type="button" className="wdChip wdChipFilo wdChipBoton" title="Quitar"
										onClick={() => set(d.cursosModo === "estos" ? "levelIds" : "excludeLevelIds", (d.cursosModo === "estos" ? d.levelIds : d.excludeLevelIds).filter((x) => x !== id))}>
										{nombreModulo[id] || "Módulo " + id} ✕</button>
								))}
							</div>
						) : null}
					</>
				) : null}
				<details className="wdMas">
					<summary>Más Filtros (horarios, periodos, modalidad, duración)</summary>
					<div className="wdGrid" style={{ marginTop: 6 }}>
						{campo("Modalidad", <select className="form-select" value={d.modality} onChange={(e) => set("modality", e.target.value)}>
							<option value="">Presencial y en Línea</option><option value="presencial">Solo Presencial</option><option value="online">Solo en Línea</option></select>)}
						{d.que !== "multimes" ? campo("Solo Cursos de N Meses", <input className="form-control" type="number" min="1" value={d.monthsTotal} onChange={(e) => set("monthsTotal", e.target.value)} />) : null}
						{campo("Horarios del Período Desde", <FechaDMY value={d.periodFrom} onChange={(v) => set("periodFrom", v)} />)}
					</div>
					<div className="wdTenue" style={{ fontSize: 13, marginTop: 6 }}>Solo Estos Periodos (vacío = todos)</div>
					<Casillas opciones={((cursos?.periods || []) as any[]).map((p) => ({ id: p.id, label: p.name, sub: "desde el " + dmy(p.start) }))} elegidos={d.periodIds} onChange={(v) => set("periodIds", v)} alto={150} />
					<div className="wdTenue" style={{ fontSize: 13, marginTop: 6 }}>Solo Estos Horarios (vacío = todos)</div>
					<Casillas opciones={horarios.map((s) => ({ id: s.id, label: horarioAmPm(s.name, horarioBonito), sub: [s.levelName, s.start ? "empieza el " + dmy(s.start) : "", s.months + (s.months === 1 ? " mes" : " meses")].filter(Boolean).join(" · ") }))} elegidos={d.scheduleIds} onChange={(v) => set("scheduleIds", v)} alto={180} />
				</details>

				<h6 className="wdSub">2 · Para Quién</h6>
				<div className="wdGrid">
					{campo("Alumnos", <select className="form-select" value={d.audience} onChange={(e) => set("audience", e.target.value)}>
						<option value="">Todos</option>{Object.keys(AUDIENCIAS).map((k) => <option key={k} value={k}>{AUDIENCIAS[k]}</option>)}</select>)}
					{campo("¿Con Quién?", <select className="form-select" value={d.linkType} onChange={(e) => set("linkType", e.target.value)}>
						<option value="">Nadie (sola)</option>{Object.keys(VINCULOS).map((k) => <option key={k} value={k}>{VINCULOS[k]}</option>)}</select>)}
					{d.linkType === "grupo" ? campo("Personas del Grupo", <input className="form-control" type="number" min="2" max="10" value={d.groupSize} onChange={(e) => set("groupSize", e.target.value)} />) : null}
				</div>

				<h6 className="wdSub">3 · Qué Da</h6>
				<div className="wdGrid">
					{campo("Descuento", <select className="form-select" value={d.que} onChange={(e) => set("que", e.target.value)}>
						<option value="percent">Porcentaje</option><option value="amount">Monto Menos</option><option value="fixed_price">Precio Fijo por Mes</option>
						<option value="multimes">Meses Regalados (paga N, regala M)</option></select>)}
					{d.que === "multimes" ? (
						<>
							{campo("Paga (meses)", <input className="form-control" type="number" min="1" value={d.monthsPaid} onChange={(e) => set("monthsPaid", e.target.value)} />)}
							{campo("Regala (meses)", <input className="form-control" type="number" min="1" value={d.monthsFree} onChange={(e) => set("monthsFree", e.target.value)} />)}
						</>
					) : campo(d.que === "percent" ? "Porcentaje (100 = gratis)" : "Monto", <input className="form-control" type="number" min="0" step="0.01" value={d.value} onChange={(e) => set("value", e.target.value)} />)}
					{d.que !== "multimes" ? campo("Desde la Mensualidad", <input className="form-control" type="number" min="1" value={d.monthFrom} onChange={(e) => set("monthFrom", e.target.value)} />) : null}
					{d.que !== "multimes" ? campo("Hasta la Mensualidad", <input className="form-control" type="number" min="1" value={d.monthTo} onChange={(e) => set("monthTo", e.target.value)} />) : null}
					{d.group === "producto" ? campo("Pide N Mensualidades Pagadas", <input className="form-control" value={d.requirePaidMonths} onChange={(e) => set("requirePaidMonths", e.target.value)} placeholder="8 o all" />) : null}
				</div>
				{d.que === "multimes" && Number(d.monthsPaid) > 0 && Number(d.monthsFree) > 0 ? (
					<div className="wdNota" style={{ fontSize: 13 }}>Solo en cursos de <b>{Number(d.monthsPaid) + Number(d.monthsFree)} meses</b>: paga {d.monthsPaid} y los últimos {d.monthsFree} quedan en $0 al pagar el adelanto completo.</div>
				) : null}
				<label className="wdCasilla"><input type="checkbox" checked={d.allMonthsInPayment} onChange={(e) => set("allMonthsInPayment", e.target.checked)} /><span>Vale pagando todas las mensualidades juntas</span></label>
				<label className="wdCasilla"><input type="checkbox" checked={d.freeSecond} onChange={(e) => set("freeSecond", e.target.checked)} /><span>Paga 1 → el 2do gratis (reingreso)</span></label>

				<h6 className="wdSub">4 · Cuándo (hora local del plantel)</h6>
				<div className="wdGrid">
					{campoVarios("Empieza", <FechaHoraDMY value={d.startsAt} onChange={(v) => set("startsAt", v)} />)}
					{campoVarios("Termina (vacío = estable)", <FechaHoraDMY value={d.endsAt} onChange={(v) => set("endsAt", v)} />)}
					{campoVarios("Franja: Desde", <HoraAmPm value={d.timeFrom} onChange={(v) => set("timeFrom", v)} />)}
					{campoVarios("Franja: Hasta", <HoraAmPm value={d.timeTo} onChange={(v) => set("timeTo", v)} />)}
				</div>
				{franjaCruza ? <div className="wdNota" style={{ fontSize: 13 }}>Cruza la medianoche: de {ampm(d.timeFrom)} a {ampm(d.timeTo)} del día siguiente (lo de después de las 12 cuenta como del día que empezó).</div> : null}
				{d.timeFrom && d.timeTo && d.timeFrom === d.timeTo ? <div className="wdAviso" style={{ fontSize: 13 }}>La franja empieza y termina a la misma hora.</div> : null}
				<div className="wdOpciones" style={{ marginTop: 6 }}>
					<span className="wdTenue" style={{ fontSize: 13 }}>Solo Estos Días:</span>
					{DIAS.map(([n, l]) => (
						<button key={"d" + n} type="button" className={"wdToggle wdToggleDia" + (d.weekdays.includes(n) ? " wdToggleOn" : "")}
							onClick={() => set("weekdays", d.weekdays.includes(n) ? d.weekdays.filter((x) => x !== n) : [...d.weekdays, n].sort())}>{l}</button>
					))}
				</div>

				<h6 className="wdSub">5 · Cupos</h6>
				<div className="wdGrid">
					{campo("Tope de Usos (vacío = sin tope)", <input className="form-control" type="number" min="1" value={d.quotaLimit} onChange={(e) => set("quotaLimit", e.target.value)} />)}
					{campo("Cada", <select className="form-select" value={d.quotaPeriod} onChange={(e) => set("quotaPeriod", e.target.value)}>
						<option value="">—</option>{Object.keys(PERIODOS).map((k) => <option key={k} value={k}>{PERIODOS[k]}</option>)}</select>)}
					{campo("Contado", <select className="form-select" value={d.quotaScope} onChange={(e) => set("quotaScope", e.target.value)}>
						<option value="plantel">Por Plantel</option><option value="compartido">Compartido Entre Planteles</option></select>)}
					{d.quotaScope === "compartido" ? campo("Llave del Cupo Compartido", <input className="form-control" value={d.quotaKey} placeholder="mismo nombre en cada plantel" onChange={(e) => set("quotaKey", e.target.value)} />) : null}
					{campo("Tope por Asesora", <input className="form-control" type="number" min="1" value={d.quotaPerAdvisor} onChange={(e) => set("quotaPerAdvisor", e.target.value)} />)}
				</div>

				<h6 className="wdSub">6 · Quién la Puede Dar</h6>
				<div className="wdGrid">
					{campo("La Da", <select className="form-select" value={d.givenBy} onChange={(e) => set("givenBy", e.target.value)}>
						<option value="asesora">Asesora (al matricular)</option><option value="supervisora">Solo Supervisora</option><option value="caja">Solo Caja (al cobrar)</option></select>)}
				</div>
				<label className="wdCasilla"><input type="checkbox" checked={d.requiresApproval} onChange={(e) => set("requiresApproval", e.target.checked)} /><span>Requiere aprobación de Supervisión o Dirección de Admisiones (queda en Auditoría)</span></label>

				<h6 className="wdSub">7 · No Se Combina Con</h6>
				<Casillas opciones={Object.keys(codigos || {}).filter((c) => !promo || c !== promo.code).map((c) => ({ id: c, label: codigos[c], sub: c }))}
					elegidos={d.notCombinable} onChange={(v) => set("notCombinable", v)} alto={160} />

				<label className="wdCasilla" style={{ marginTop: 8 }}><input type="checkbox" checked={d.active} onChange={(e) => set("active", e.target.checked)} /><span>Activa</span></label>
				{campo("Lo que Dice la Guía (para asesoras y Caja)", <textarea className="form-control" rows={2} value={d.conditions} onChange={(e) => set("conditions", e.target.value)} />)}
				{promo ? campo("Por Qué Cambia (queda en el historial)", <input className="form-control" value={d.reason} placeholder="Obligatorio: 10 letras o más" onChange={(e) => set("reason", e.target.value)} />) : null}
				{error ? <div className="wdAviso">{error}</div> : null}
				{!valido ? <div className="wdAviso" style={{ fontSize: 13 }}>Para guardar falta: {faltan.join(" · ")}.</div> : null}
				<div className="wdBotones">
					<button type="button" className="wdBoton" disabled={busy} onClick={onCancel}>Cancelar</button>
					<button type="button" className="wdBoton wdPrimario" disabled={busy || !valido} onClick={save}>{busy ? "Guardando…" : "Guardar"}</button>
				</div>
			</div>
		);
	};

/** Copiar una promo a otros planteles: los cursos se buscan por nombre allá; lo que no existe se reporta. */
export const CopiarPromo: React.FC<{ campusId: number; promo: Promo; campuses: any[]; onDone: () => void }> = ({ campusId, promo, campuses, onDone }) => {
	const [destinos, setDestinos] = useState<number[]>([]);
	const [res, setRes] = useState<any[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const opciones = campuses.filter((c) => c.id !== campusId && c.canEdit);
	const unaVez = useUnaALaVez();
	const copiar = () => unaVez(copiarAhora);
	const copiarAhora = async () => {
		try {
			setBusy(true); setError(null);
			const r = await http.paymentService.copyPromotion(campusId, promo.id, destinos);
			setRes(r.data.results || []);
			onDone();
		} catch (e) {
			setError(serverError(e));
		} finally {
			setBusy(false);
		}
	};
	return (
		<div className="wdEditor">
			<b>Copiar «{promo.name}» a Otros Planteles</b>
			<Casillas opciones={opciones.map((c) => ({ id: c.id, label: c.name }))} elegidos={destinos} onChange={setDestinos} alto={180} />
			{res ? res.map((r: any) => {
				const c = campuses.find((x) => x.id === r.campusId);
				return <div key={"r" + r.campusId} className={r.ok ? "wdNota" : "wdAviso"} style={{ fontSize: 13 }}>{c?.name || r.campusId}: {r.ok ? "copiada" : r.why}
					{r.notCopied && r.notCopied.length ? " · no existen allá: " + r.notCopied.join(", ") : ""}{r.note ? " · " + r.note : ""}</div>;
			}) : null}
			{error ? <div className="wdAviso">{error}</div> : null}
			<div className="wdBotones">
				<button type="button" className="wdBoton wdPrimario" disabled={busy || !destinos.length} onClick={copiar}>{busy ? "Copiando…" : "Copiar"}</button>
			</div>
		</div>
	);
};

/** Reporte de usos por promo: confirmados, apartados, por excepción, monto descontado, cupo; por mes y por asesora. */
export const ReportePromos: React.FC<{ campusId: number; sym: string }> = ({ campusId, sym }) => {
	const hoy = new Date();
	const iso = (x: Date) => x.getFullYear() + "-" + String(x.getMonth() + 1).padStart(2, "0") + "-" + String(x.getDate()).padStart(2, "0");
	const [desde, setDesde] = useState<string>(iso(new Date(hoy.getFullYear(), hoy.getMonth(), 1)));
	const [hasta, setHasta] = useState<string>(iso(hoy));
	const [data, setData] = useState<any>(null);
	const [error, setError] = useState<string | null>(null);
	const [abierto, setAbierto] = useState<number | null>(null);
	React.useEffect(() => {
		setData(null);
		http.paymentService.promotionReport(campusId, desde, hasta).then((r: any) => { setData(r.data); setError(null); }).catch((e: any) => setError(serverError(e)));
	}, [campusId, desde, hasta]);
	const num = (n: number) => (Math.round((Number(n) || 0) * 100) / 100).toLocaleString("es-MX", { maximumFractionDigits: 2 });
	const n = (k: number, uno: string, varios: string) => k + " " + (k === 1 ? uno : varios);
	return (
		<>
			<div className="wdFiltros">
				<label className="wdCampo"><span>Desde</span><input className="form-control" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
				<label className="wdCampo"><span>Hasta</span><input className="form-control" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
			</div>
			{error ? <div className="wdAviso">{error}</div> : null}
			{data === null ? <div className="loader" /> : (
				<>
					<div className="wdNota">{n(data.totals.confirmed, "uso confirmado", "usos confirmados")} · {n(data.totals.reserved, "apartado", "apartados")} · {sym}{num(data.totals.discounted)} descontados en total
						{data.totals.toGift > 0 ? <> · {sym}{num(data.totals.toGift)} por regalar <small>(multimes que esperan su pago adelantado)</small></> : null}
						{data.totals.lost > 0 ? <div style={{ fontSize: 13 }}>{n(data.totals.lost, "acuerdo perdido cuenta", "acuerdos perdidos cuentan")} como uso, con lo que sí se descontó antes de perderse.</div> : null}</div>
					{!data.data.length ? <div className="wdTenue">Sin usos en estas fechas.</div> : data.data.map((p: any) => (
						<React.Fragment key={"rp" + p.promotionId}>
							<button type="button" className="wdFila wdFilaBoton" onClick={() => setAbierto(abierto === p.promotionId ? null : p.promotionId)}>
								<div className="wdNombre"><b>{p.name}</b><small>{p.code}</small></div>
								<div>{n(p.confirmed, "confirmado", "confirmados")}{p.reserved ? " · " + n(p.reserved, "apartado", "apartados") : ""}{p.byException ? " · " + p.byException + " por excepción" : ""}{p.lost ? " · " + n(p.lost, "perdido", "perdidos") : ""}</div>
								<div>{sym}{num(p.discounted)}{p.toGift > 0 ? <small style={{ display: "block" }}>{sym}{num(p.toGift)} por regalar</small> : null}</div>
								<div>{p.quota?.text || "Sin tope"}</div>
							</button>
							{abierto === p.promotionId ? (
								<div className="wdDetalle" style={{ fontSize: 13 }}>
									<div><b>Por mes:</b> {Object.keys(p.byMonth).map((m) => m + ": " + p.byMonth[m]).join(" · ")}</div>
									<div><b>Por asesora:</b> {Object.keys(p.byAdvisor).map((a) => a + ": " + p.byAdvisor[a]).join(" · ")}</div>
								</div>
							) : null}
						</React.Fragment>
					))}
				</>
			)}
		</>
	);
};

export default EditorPromo;
